import { RefObject, useCallback, useEffect, useRef } from 'react';

import { ANIMATION_DURATION } from '../Carousel.constants';

interface IUseCarouselAnimationParams {
  carouselRef: RefObject<HTMLDivElement | null>;
}

interface IUseCarouselAnimationReturn {
  currentPositionRef: RefObject<number>;
  targetPositionRef: RefObject<number>;
  animateTo: (targetPosition: number, adjustPosition?: number) => void;
  stopAnimation: () => void;
  setPositionImmediate: (position: number) => void;
}

function easeOutQuint(t: number): number {
  return 1 - Math.pow(1 - t, 5);
}

export function useCarouselAnimation({
  carouselRef,
}: IUseCarouselAnimationParams): IUseCarouselAnimationReturn {
  const animationRef = useRef<number | null>(null);
  const targetPositionRef = useRef(0);
  const currentPositionRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const startPositionRef = useRef<number>(0);

  const applyTransform = useCallback(
    (position: number): void => {
      if (carouselRef.current) {
        carouselRef.current.style.transform = `translateX(${position}px)`;
      }
    },
    [carouselRef]
  );

  const animate = useCallback(
    (timestamp: number): void => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
        startPositionRef.current = currentPositionRef.current;
      }

      const elapsedTime = timestamp - startTimeRef.current;

      if (elapsedTime < ANIMATION_DURATION) {
        const progress = elapsedTime / ANIMATION_DURATION;
        const easeProgress = easeOutQuint(progress);

        currentPositionRef.current =
          startPositionRef.current +
          (targetPositionRef.current - startPositionRef.current) * easeProgress;

        applyTransform(currentPositionRef.current);
        animationRef.current = requestAnimationFrame(animate);
      } else {
        currentPositionRef.current = targetPositionRef.current;
        applyTransform(currentPositionRef.current);
        animationRef.current = null;
        startTimeRef.current = null;
      }
    },
    [applyTransform]
  );

  const stopAnimation = useCallback((): void => {
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    startTimeRef.current = null;
    startPositionRef.current = 0;
  }, []);

  const animateTo = useCallback(
    (targetPosition: number, adjustPosition?: number): void => {
      stopAnimation();

      targetPositionRef.current = targetPosition;

      if (adjustPosition !== undefined) {
        currentPositionRef.current += adjustPosition;
        targetPositionRef.current += adjustPosition;
      }

      animationRef.current = requestAnimationFrame(animate);
    },
    [stopAnimation, animate]
  );

  const setPositionImmediate = useCallback(
    (position: number): void => {
      currentPositionRef.current = position;
      targetPositionRef.current = position;
      applyTransform(position);
    },
    [applyTransform]
  );

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    currentPositionRef,
    targetPositionRef,
    animateTo,
    stopAnimation,
    setPositionImmediate,
  };
}
